import { useEffect, useState } from "react";
import { Mail, Search, RotateCw, X, Eye, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STATUS_COLORS = {
    sent: "bg-green-100 text-green-700",
    mocked: "bg-orange-100 text-orange-700",
    error: "bg-red-100 text-red-700",
};
const STATUS_ICON = {
    sent: CheckCircle2,
    mocked: Clock,
    error: AlertCircle,
};

function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function PreviewDialog({ logId, onClose }) {
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!logId) return;
        api.get(`/admin/email-logs/${logId}`)
            .then((r) => setDoc(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [logId]);

    if (!logId) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="email-preview-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-3xl bg-white shadow-premium-lg flex flex-col"
            >
                <div className="flex items-start justify-between border-b border-brand-line p-6">
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-orange">Email logé</div>
                        <h3 className="mt-1 font-display text-2xl">{doc?.subject || "Chargement…"}</h3>
                        {doc && (
                            <div className="mt-2 text-xs text-brand-smoke">
                                Template : <code className="font-semibold text-brand-ink">{doc.template}</code> · vers <strong className="text-brand-ink">{doc.to}</strong>
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="text-brand-smoke hover:text-brand-ink">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-auto bg-[#FAFAFA] p-6">
                    {loading ? <div className="text-center text-brand-smoke">Chargement…</div> :
                     doc ? (
                        <div className="rounded-xl border border-brand-line bg-white" data-testid="email-preview-html">
                            <iframe
                                srcDoc={doc.html}
                                title="Aperçu email"
                                className="h-[480px] w-full rounded-xl"
                                sandbox=""
                            />
                        </div>
                     ) : null}
                </div>
            </motion.div>
        </div>
    );
}

export default function AdminEmailLogs() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState(null);
    const [filterTemplate, setFilterTemplate] = useState("");
    const [filterTo, setFilterTo] = useState("");
    const [previewId, setPreviewId] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchAll = async () => {
        try {
            const params = new URLSearchParams();
            if (filterTemplate) params.set("template", filterTemplate);
            if (filterTo) params.set("to", filterTo);
            const [list, c] = await Promise.all([
                api.get(`/admin/email-logs?${params.toString()}`),
                api.get("/admin/email-logs/stats/counts"),
            ]);
            setItems(list.data);
            setCounts(c.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterTemplate]);

    const resend = async (id) => {
        try {
            await api.post(`/admin/email-logs/${id}/resend`);
            toast.success("Email renvoyé (mocké si Resend désactivé)");
            fetchAll();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const templates = Array.from(new Set(items.map((i) => i.template))).sort();

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-admin-email-logs">
            <div className="flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">Communication</div>
                    <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">Journal des emails</h1>
                    <p className="mt-3 max-w-2xl text-brand-smoke">
                        Trace de tous les emails envoyés ou mockés (Resend non configuré). Cliquez sur une ligne pour prévisualiser le rendu HTML.
                    </p>
                </div>
                <div className="hidden h-16 w-16 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange md:flex">
                    <Mail className="h-7 w-7" />
                </div>
            </div>

            {counts && (
                <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="email-logs-counts">
                    <div className="rounded-2xl border border-brand-line bg-white p-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Total</div>
                        <div className="mt-2 font-display text-3xl">{counts.total}</div>
                    </div>
                    <div className="rounded-2xl border border-green-200 bg-green-50 p-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-green-700">Envoyés (Resend)</div>
                        <div className="mt-2 font-display text-3xl text-green-800">{counts.sent}</div>
                    </div>
                    <div className="rounded-2xl border border-orange-200 bg-orange-50 p-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-orange-700">Mockés</div>
                        <div className="mt-2 font-display text-3xl text-orange-800">{counts.mocked}</div>
                    </div>
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-red-700">Erreurs</div>
                        <div className="mt-2 font-display text-3xl text-red-800">{counts.errors}</div>
                    </div>
                </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[260px]">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />
                    <input
                        value={filterTo}
                        onChange={(e) => setFilterTo(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && fetchAll()}
                        placeholder="Filtrer par destinataire…"
                        data-testid="email-logs-filter-to"
                        className="w-full rounded-full border border-brand-line bg-white py-2 pl-10 pr-4 text-sm outline-none focus:border-brand-orange"
                    />
                </div>
                <select
                    value={filterTemplate}
                    onChange={(e) => setFilterTemplate(e.target.value)}
                    data-testid="email-logs-filter-template"
                    className="rounded-full border border-brand-line bg-white px-4 py-2 text-sm outline-none focus:border-brand-orange"
                >
                    <option value="">Tous templates</option>
                    {templates.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>

            <div className="mt-6 overflow-hidden rounded-2xl border border-brand-line bg-white shadow-premium">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-brand-line bg-[#FAFAFA] text-left text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                            <th className="px-6 py-3">Date</th>
                            <th className="px-6 py-3">Template</th>
                            <th className="px-6 py-3">Destinataire</th>
                            <th className="px-6 py-3">Sujet</th>
                            <th className="px-6 py-3">Statut</th>
                            <th className="px-6 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-line">
                        {loading ? (
                            <tr><td colSpan={6} className="py-10 text-center text-brand-smoke">Chargement…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={6} className="py-10 text-center text-brand-smoke">Aucun email enregistré.</td></tr>
                        ) : items.map((it) => {
                            const Icon = STATUS_ICON[it.status] || Clock;
                            return (
                                <tr key={it.id} className="hover:bg-[#FAFAFA]" data-testid={`email-log-row-${it.id}`}>
                                    <td className="px-6 py-3 text-xs text-brand-smoke">{formatDate(it.created_at)}</td>
                                    <td className="px-6 py-3"><code className="rounded bg-brand-line/40 px-2 py-0.5 text-xs">{it.template}</code></td>
                                    <td className="px-6 py-3">{it.to}</td>
                                    <td className="px-6 py-3 text-brand-ink/80">{it.subject}</td>
                                    <td className="px-6 py-3">
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[it.status]}`}>
                                            <Icon className="h-3 w-3" /> {it.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-3 text-right">
                                        <button
                                            onClick={() => setPreviewId(it.id)}
                                            data-testid={`email-log-preview-${it.id}`}
                                            className="mr-1 inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                                            title="Aperçu"
                                        >
                                            <Eye className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                            onClick={() => resend(it.id)}
                                            data-testid={`email-log-resend-${it.id}`}
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                                            title="Renvoyer"
                                        >
                                            <RotateCw className="h-3.5 w-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <PreviewDialog logId={previewId} onClose={() => setPreviewId(null)} />
        </div>
    );
}
